/**
 * Client-side image compression utilities
 *
 * Provides functions for compressing images before upload, checking file types,
 * and formatting file sizes for display.
 */
/* eslint-disable no-console */

import heic2any from 'heic2any';
import imageCompression from 'browser-image-compression';

/**
 * Options for image compression
 */
export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  fileType?: string;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: 'image/jpeg',
};

/**
 * Compress an image file to reduce size before upload
 *
 * @param file - The image file to compress
 * @param options - Compression options (merged with defaults)
 * @returns Compressed image file
 *
 * @remarks
 * - Default max size: 2MB
 * - Default max dimension: 1920px
 * - Output format: JPEG
 * - Uses web worker for better performance
 * - Returns original file if compression fails
 *
 * @example
 * ```typescript
 * const compressed = await compressImage(file, {
 *   maxSizeMB: 1,
 *   maxWidthOrHeight: 1024,
 * });
 * ```
 */
export async function compressImage(file: File, options: CompressionOptions = {}): Promise<File> {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  try {
    if (file.type === 'image/heic' || file.type === 'image/heif') {
      const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
      const convertedFile = new File([convertedBlob as Blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
        type: 'image/jpeg',
      });
      const compressedFile = await imageCompression(convertedFile, mergedOptions);
      return compressedFile;
    }

    const compressedFile = await imageCompression(file, mergedOptions);
    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    // Return original file if compression fails
    return file;
  }
}

/**
 * Check if a file is an image based on MIME type
 *
 * @param file - The file to check
 * @returns true if file type starts with 'image/', false otherwise
 *
 * @example
 * ```typescript
 * if (isImageFile(file)) {
 *   const compressed = await compressImage(file);
 * }
 * ```
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Check if a file is a video based on MIME type
 *
 * @param file - The file to check
 * @returns true if file type starts with 'video/', false otherwise
 *
 * @example
 * ```typescript
 * if (isVideoFile(file)) {
 *   console.log('This is a video file');
 * }
 * ```
 */
export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

/**
 * Format file size in bytes to human-readable string
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB", "512 KB")
 *
 * @example
 * ```typescript
 * formatFileSize(1024)      // "1 KB"
 * formatFileSize(1536000)   // "1.5 MB"
 * formatFileSize(0)         // "0 Bytes"
 * ```
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
